import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  Badge,
  Chip,
  Collapse,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FolderIcon from '@mui/icons-material/Folder';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import NotesIcon from '@mui/icons-material/Notes';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import GroupsIcon from '@mui/icons-material/Groups';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SettingsIcon from '@mui/icons-material/Settings';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EmailIcon from '@mui/icons-material/Email';
import RateReviewIcon from '@mui/icons-material/RateReview';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BackupIcon from '@mui/icons-material/Backup';
import TableChartIcon from '@mui/icons-material/TableChart';
import HandymanIcon from '@mui/icons-material/Handyman';
import TuneIcon from '@mui/icons-material/Tune';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import BugReportIcon from '@mui/icons-material/BugReport';
import ForumIcon from '@mui/icons-material/Forum';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScienceIcon from '@mui/icons-material/Science';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { useSandboxFreigaben } from '../../hooks/useSandboxFreigaben';
import { useEasterEgg } from '../../hooks/EasterEggContext';
import { useNotification } from '../../hooks/NotificationContext';
import { schwachstellenService } from '../../services/schwachstellenService';
import { getVersionInfo } from '../../services/healthService';
import { DEV_MODE_LABEL } from '../../utils/easterEggs';
import { useSaison } from '../../hooks/SaisonContext';
import * as P from '../../constants/permissions';

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

const DRAWER_WIDTH = 260;

const STORAGE_KEY = 'stark-sidebar-collapsed';

function loadCollapsed(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveCollapsed(state: Record<string, boolean>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen = false, onMobileClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user } = useAuth();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const { devMode, logoSpin, registerVersionClick } = useEasterEgg();
  const { copy: saison } = useSaison();
  const { showInfo } = useNotification();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const hasReviewPermission = hasPermission(P.SCHWACHSTELLEN_REVIEW);
  const { count: freigabenCount } = useSandboxFreigaben();
  const [reviewCount, setReviewCount] = useState(0);
  const [version, setVersion] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(loadCollapsed);

  const toggleSection = (key: string) => {
    setCollapsed((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      saveCollapsed(next);
      return next;
    });
  };

  const isSectionOpen = (key: string, defaultOpen = true) =>
    collapsed[key] === undefined ? defaultOpen : !collapsed[key];

  const fetchReviewCount = useCallback(async () => {
    if (!hasReviewPermission) return;
    try {
      const count = await schwachstellenService.reviewQueueCount();
      setReviewCount(count);
    } catch {
      // silently ignore
    }
  }, [hasReviewPermission]);

  useEffect(() => {
    fetchReviewCount();
    if (!hasReviewPermission) return;
    const interval = setInterval(fetchReviewCount, 60_000);
    return () => clearInterval(interval);
  }, [fetchReviewCount, hasReviewPermission]);

  useEffect(() => {
    getVersionInfo()
      .then((info) => setVersion(info.version))
      .catch(() => setVersion(null));
  }, []);

  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.primary.main;
  const bgSidebar = isDark ? '#1c203b' : '#ffffff';
  const borderColor = theme.palette.divider;
  const hoverBg = isDark ? '#2c3358' : '#eef1f5';
  const sidebarText = isDark ? '#b0bec5' : '#3a4a5a';
  const sidebarLabel = isDark ? '#556677' : '#8899aa';
  const sidebarIcon = isDark ? '#667788' : '#6b7c8d';
  const versionText = isDark ? '#445566' : '#9aa5b0';

  const navItemSx = {
    borderRadius: 2,
    mb: 0.3,
    py: 1,
    transition: 'box-shadow 0.2s ease-out, background-color 0.15s',
    '&.Mui-selected': {
      bgcolor: hoverBg,
      borderLeft: `3px solid ${accent}`,
      '& .MuiListItemIcon-root': { color: accent },
      '& .MuiListItemText-primary': { color: accent, fontWeight: 600 },
    },
    '&:hover': {
      bgcolor: hoverBg,
    },
  };

  useEffect(() => {
    let prev: HTMLElement | null = null;
    const handler = (e: Event) => {
      const { path, opacity } = (e as CustomEvent).detail;
      if (prev) {
        prev.style.boxShadow = '';
        prev = null;
      }
      if (path && opacity > 0) {
        const el = document.querySelector(
          `[data-nav-path="${path}"]`,
        ) as HTMLElement | null;
        if (el) {
          el.style.boxShadow = `0 0 0 2.5px rgba(239, 68, 68, ${opacity})`;
          prev = el;
        }
      }
    };
    document.addEventListener('docs-sidebar-highlight', handler);
    return () => {
      document.removeEventListener('docs-sidebar-highlight', handler);
      if (prev) prev.style.boxShadow = '';
    };
  }, []);

  const navTextProps = {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: sidebarText,
  };

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const handleNavigate = (path: string, e?: React.MouseEvent) => {
    if (e && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      window.open(path, '_blank');
      return;
    }
    navigate(path);
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  const navProps = (path: string) => ({
    onClick: (e: React.MouseEvent) => handleNavigate(path, e),
    onMouseDown: (e: React.MouseEvent) => {
      if (e.button === 1) {
        e.preventDefault();
      }
    },
    onMouseUp: (e: React.MouseEvent) => {
      if (e.button === 1) {
        e.preventDefault();
        window.open(path, '_blank');
      }
    },
  });

  const sectionHeaderSx = {
    fontSize: '0.65rem',
    fontWeight: 700,
    color: sidebarLabel,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    px: 2.5,
    pt: 1,
    pb: 1,
    cursor: 'pointer',
    userSelect: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '&:hover': { color: sidebarText },
    transition: 'color 0.15s',
  } as const;

  const showProjekteAudits = hasAnyPermission(P.PROJEKTE_VIEW, P.AUDITS_VIEW, P.SCHWACHSTELLEN_REVIEW);
  const showWorkbench = hasPermission(P.WORKBENCH_USE);
  const showVerwaltung = hasAnyPermission(P.ANGEBOTE_VIEW, P.RECHNUNGEN_VIEW, P.RECHNUNGEN_SETTINGS, P.KUNDEN_VIEW, P.MITARBEITER_VIEW, P.URLAUB_VIEW_ALL, P.VERWALTUNG_STAMMDATEN, P.DATA_EXPORT, P.DATA_IMPORT, P.UPLOAD_SESSIONS_VIEW);
  const showAdmin = hasPermission(P.VERWALTUNG_MANAGE);

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? mobileOpen : true}
      onClose={onMobileClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: { xs: 0, md: DRAWER_WIDTH },
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          bgcolor: bgSidebar,
          borderRight: `1px solid ${borderColor}`,
          overflowX: 'hidden',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Logo area */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2.5,
          py: 2.5,
          cursor: 'pointer',
          flexShrink: 0,
        }}
        {...navProps('/')}
      >
        <Box
          component="img"
          key={logoSpin}
          src="/stark_logo.png"
          alt="STARK IT-Security"
          sx={{
            maxWidth: '100%',
            height: 52,
            objectFit: 'contain',
            ...(logoSpin > 0
              ? {
                  animation: 'stark-logo-spin 0.8s ease',
                  '@keyframes stark-logo-spin': {
                    from: { transform: 'rotate(0deg)' },
                    to: { transform: 'rotate(360deg)' },
                  },
                }
              : {}),
          }}
        />
      </Box>

      {saison && (
        <Typography
          sx={{
            px: 2,
            pb: 1.5,
            textAlign: 'center',
            fontSize: '0.75rem',
            color: versionText,
            flexShrink: 0,
          }}
        >
          {saison.zeichen} {saison.label}
        </Typography>
      )}

      <Divider sx={{ borderColor, mx: 1.5, flexShrink: 0 }} />

      {/* Scrollable nav content */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>

        {/* ── Mein Bereich ── */}
        <Typography
          sx={{ ...sectionHeaderSx, pt: 2 }}
          onClick={() => toggleSection('meinBereich')}
        >
          <span>Mein Bereich</span>
          {isSectionOpen('meinBereich') ? <ExpandLessIcon sx={{ fontSize: '1rem' }} /> : <ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
        </Typography>

        <Collapse in={isSectionOpen('meinBereich')} timeout="auto">
          <List sx={{ px: 1.5 }}>
            <ListItemButton
              data-nav-path="/"
              selected={isActive('/')}
              {...navProps('/')}
              sx={navItemSx}
            >
              <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><DashboardIcon /></ListItemIcon>
              <ListItemText primary="Dashboard" primaryTypographyProps={navTextProps} />
            </ListItemButton>

            <ListItemButton
              data-nav-path="/zeiterfassung"
              selected={isActive('/zeiterfassung')}
              {...navProps('/zeiterfassung')}
              sx={navItemSx}
            >
              <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><AccessTimeIcon /></ListItemIcon>
              <ListItemText primary="Zeiterfassung" primaryTypographyProps={navTextProps} />
            </ListItemButton>

            {hasAnyPermission(P.EINGANGSBELEGE_OWN, P.EINGANGSBELEGE_VIEW_ALL, P.EINGANGSBELEGE_MANAGE) && (
              <ListItemButton
                data-nav-path="/eingangsbelege"
                selected={isActive('/eingangsbelege')}
                {...navProps('/eingangsbelege')}
                sx={navItemSx}
              >
                <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><MoveToInboxIcon /></ListItemIcon>
                <ListItemText primary="Eingangsbelege" primaryTypographyProps={navTextProps} />
              </ListItemButton>
            )}

            <ListItemButton
              data-nav-path="/urlaub"
              selected={isActive('/urlaub')}
              {...navProps('/urlaub')}
              sx={navItemSx}
            >
              <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><BeachAccessIcon /></ListItemIcon>
              <ListItemText primary="Abwesenheiten" primaryTypographyProps={navTextProps} />
            </ListItemButton>

            {hasPermission(P.ANWESENHEIT_VIEW) && (
              <ListItemButton
                data-nav-path="/anwesenheit"
                selected={isActive('/anwesenheit')}
                {...navProps('/anwesenheit')}
                sx={navItemSx}
              >
                <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><GroupsIcon /></ListItemIcon>
                <ListItemText primary="Anwesenheit" primaryTypographyProps={navTextProps} />
              </ListItemButton>
            )}

            <ListItemButton
              data-nav-path="/hall-of-fame"
              selected={isActive('/hall-of-fame')}
              {...navProps('/hall-of-fame')}
              sx={navItemSx}
            >
              <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><EmojiEventsIcon /></ListItemIcon>
              <ListItemText primary="Hall of Fame" primaryTypographyProps={navTextProps} />
            </ListItemButton>

            <ListItemButton
              data-nav-path="/isolierte-umgebung"
              selected={isActive('/isolierte-umgebung')}
              {...navProps('/isolierte-umgebung')}
              sx={navItemSx}
            >
              <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><ScienceIcon /></ListItemIcon>
              <ListItemText primary="Isolierte Umgebung" primaryTypographyProps={navTextProps} />
              {freigabenCount > 0 && (
                <Tooltip title={`${freigabenCount} Audit(s)/Finding(s) in die isolierte Umgebung freigegeben`}>
                  <Chip
                    size="small"
                    icon={<VisibilityIcon sx={{ fontSize: '0.85rem !important' }} />}
                    label={freigabenCount}
                    color="warning"
                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                    data-testid="sidebar-freigaben-badge"
                  />
                </Tooltip>
              )}
            </ListItemButton>

            {hasPermission(P.DISKUSSION_VIEW) && (
              <ListItemButton
                data-nav-path="/diskussion"
                selected={isActive('/diskussion')}
                {...navProps('/diskussion')}
                sx={navItemSx}
              >
                <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><ForumIcon /></ListItemIcon>
                <ListItemText primary="Diskussion" primaryTypographyProps={navTextProps} />
              </ListItemButton>
            )}
          </List>
        </Collapse>

        {/* ── Projekte & Audits ── */}
        {showProjekteAudits && (
          <>
            <Divider sx={{ borderColor, mx: 1.5, my: 1 }} />

            <Typography
              sx={sectionHeaderSx}
              onClick={() => toggleSection('projekteAudits')}
            >
              <span>Projekte & Audits</span>
              {isSectionOpen('projekteAudits') ? <ExpandLessIcon sx={{ fontSize: '1rem' }} /> : <ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
            </Typography>

            <Collapse in={isSectionOpen('projekteAudits')} timeout="auto">
              <List sx={{ px: 1.5 }}>
                {hasPermission(P.PROJEKTE_VIEW) && (
                  <ListItemButton
                    data-nav-path="/projekte"
                    selected={isActive('/projekte')}
                    {...navProps('/projekte')}
                    sx={navItemSx}
                  >
                    <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><FolderIcon /></ListItemIcon>
                    <ListItemText primary="Projekte" primaryTypographyProps={navTextProps} />
                  </ListItemButton>
                )}

                {hasPermission(P.PROJEKTE_VIEW) && (
                  <ListItemButton
                    data-nav-path="/kanban"
                    selected={isActive('/kanban')}
                    {...navProps('/kanban')}
                    sx={navItemSx}
                  >
                    <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><ViewColumnIcon /></ListItemIcon>
                    <ListItemText primary="Kanban" primaryTypographyProps={navTextProps} />
                  </ListItemButton>
                )}

                {hasPermission(P.PROJEKTE_VIEW) && (
                  <ListItemButton
                    data-nav-path="/auditkalender"
                    selected={isActive('/auditkalender')}
                    {...navProps('/auditkalender')}
                    sx={navItemSx}
                  >
                    <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><CalendarMonthIcon /></ListItemIcon>
                    <ListItemText primary="Auditkalender" primaryTypographyProps={navTextProps} />
                  </ListItemButton>
                )}

                {hasPermission(P.AUDITS_VIEW) && (
                  <ListItemButton
                    data-nav-path="/audits"
                    selected={isActive('/audits')}
                    {...navProps('/audits')}
                    sx={navItemSx}
                  >
                    <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><SecurityIcon /></ListItemIcon>
                    <ListItemText primary="Audits" primaryTypographyProps={navTextProps} />
                  </ListItemButton>
                )}

                {hasReviewPermission && (
                  <ListItemButton
                    data-nav-path="/reviews"
                    selected={isActive('/reviews')}
                    {...navProps('/reviews')}
                    sx={navItemSx}
                  >
                    <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}>
                      <Badge
                        badgeContent={reviewCount}
                        color="warning"
                        max={99}
                        sx={{
                          '& .MuiBadge-badge': {
                            fontSize: '0.65rem',
                            height: 16,
                            minWidth: 16,
                            fontWeight: 700,
                          },
                        }}
                      >
                        <RateReviewIcon />
                      </Badge>
                    </ListItemIcon>
                    <ListItemText primary="Reviews" primaryTypographyProps={navTextProps} />
                  </ListItemButton>
                )}
              </List>
            </Collapse>
          </>
        )}

        {/* ── Workbench ── */}
        {showWorkbench && (
          <>
            <Divider sx={{ borderColor, mx: 1.5, my: 1 }} />

            <Typography
              sx={sectionHeaderSx}
              onClick={() => toggleSection('workbench')}
            >
              <span>Workbench</span>
              {isSectionOpen('workbench') ? <ExpandLessIcon sx={{ fontSize: '1rem' }} /> : <ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
            </Typography>

            <Collapse in={isSectionOpen('workbench')} timeout="auto">
              <List sx={{ px: 1.5 }}>
                <ListItemButton
                  data-nav-path="/workbench"
                  selected={isActive('/workbench')}
                  {...navProps('/workbench')}
                  sx={navItemSx}
                >
                  <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><HandymanIcon /></ListItemIcon>
                  <ListItemText primary="Toolbox" primaryTypographyProps={navTextProps} />
                </ListItemButton>
              </List>
            </Collapse>
          </>
        )}

        {/* ── Verwaltung ── */}
        {showVerwaltung && (
          <>
            <Divider sx={{ borderColor, mx: 1.5, my: 1 }} />

            <Typography
              sx={sectionHeaderSx}
              onClick={() => toggleSection('verwaltung')}
            >
              <span>Verwaltung</span>
              {isSectionOpen('verwaltung') ? <ExpandLessIcon sx={{ fontSize: '1rem' }} /> : <ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
            </Typography>

            <Collapse in={isSectionOpen('verwaltung')} timeout="auto">
              <List sx={{ px: 1.5 }}>
                {hasPermission(P.ANGEBOTE_VIEW) && (
                  <ListItemButton
                    data-nav-path="/angebote"
                    selected={isActive('/angebote')}
                    {...navProps('/angebote')}
                    sx={navItemSx}
                  >
                    <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><RequestQuoteIcon /></ListItemIcon>
                    <ListItemText primary="Angebote" primaryTypographyProps={navTextProps} />
                  </ListItemButton>
                )}

                {hasPermission(P.RECHNUNGEN_VIEW) && (
                  <ListItemButton
                    data-nav-path="/rechnungen"
                    selected={isActive('/rechnungen') && !isActive('/rechnungen/textvorlagen')}
                    {...navProps('/rechnungen')}
                    sx={navItemSx}
                  >
                    <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><ReceiptIcon /></ListItemIcon>
                    <ListItemText primary="Rechnungen" primaryTypographyProps={navTextProps} />
                  </ListItemButton>
                )}

                {hasPermission(P.RECHNUNGEN_SETTINGS) && (
                  <ListItemButton
                    data-nav-path="/rechnungen/textvorlagen"
                    selected={isActive('/rechnungen/textvorlagen')}
                    {...navProps('/rechnungen/textvorlagen')}
                    sx={navItemSx}
                  >
                    <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><NotesIcon /></ListItemIcon>
                    <ListItemText primary="Rechnungsvorlagen" primaryTypographyProps={navTextProps} />
                  </ListItemButton>
                )}

                {hasPermission(P.KUNDEN_VIEW) && (
                  <ListItemButton
                    data-nav-path="/kunden"
                    selected={isActive('/kunden')}
                    {...navProps('/kunden')}
                    sx={navItemSx}
                  >
                    <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><BusinessIcon /></ListItemIcon>
                    <ListItemText primary="Kunden" primaryTypographyProps={navTextProps} />
                  </ListItemButton>
                )}

                {hasPermission(P.MITARBEITER_VIEW) && (
                  <ListItemButton
                    data-nav-path="/mitarbeiter"
                    selected={isActive('/mitarbeiter')}
                    {...navProps('/mitarbeiter')}
                    sx={navItemSx}
                  >
                    <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><PeopleIcon /></ListItemIcon>
                    <ListItemText primary="Mitarbeiter" primaryTypographyProps={navTextProps} />
                  </ListItemButton>
                )}

                {hasPermission(P.URLAUB_VIEW_ALL) && (
                  <ListItemButton
                    data-nav-path="/urlaub-uebersicht"
                    selected={isActive('/urlaub-uebersicht')}
                    {...navProps('/urlaub-uebersicht')}
                    sx={navItemSx}
                  >
                    <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><EventAvailableIcon /></ListItemIcon>
                    <ListItemText primary="Abwesenheitsübersicht" primaryTypographyProps={navTextProps} />
                  </ListItemButton>
                )}

                {hasPermission(P.VERWALTUNG_STAMMDATEN) && (
                  <ListItemButton
                    data-nav-path="/verwaltung"
                    selected={isActive('/verwaltung')}
                    {...navProps('/verwaltung')}
                    sx={navItemSx}
                  >
                    <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><SettingsIcon /></ListItemIcon>
                    <ListItemText primary="Stammdaten" primaryTypographyProps={navTextProps} />
                  </ListItemButton>
                )}

                {hasAnyPermission(P.DATA_EXPORT, P.DATA_IMPORT) && (
                  <ListItemButton
                    data-nav-path="/daten"
                    selected={isActive('/daten')}
                    {...navProps('/daten')}
                    sx={navItemSx}
                  >
                    <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><ImportExportIcon /></ListItemIcon>
                    <ListItemText primary="Datenimport/-export" primaryTypographyProps={navTextProps} />
                  </ListItemButton>
                )}

                {hasPermission(P.UPLOAD_SESSIONS_VIEW) && (
                  <ListItemButton
                    data-nav-path="/kundenuploads"
                    selected={isActive('/kundenuploads')}
                    {...navProps('/kundenuploads')}
                    sx={navItemSx}
                  >
                    <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><CloudUploadIcon /></ListItemIcon>
                    <ListItemText primary="Kundenuploads" primaryTypographyProps={navTextProps} />
                  </ListItemButton>
                )}
              </List>
            </Collapse>
          </>
        )}

        {/* ── Admin ── */}
        {showAdmin && (
          <>
            <Divider sx={{ borderColor, mx: 1.5, my: 1 }} />

            <Typography
              sx={sectionHeaderSx}
              onClick={() => toggleSection('admin')}
            >
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AdminPanelSettingsIcon sx={{ fontSize: '0.85rem' }} />
                <span>Admin</span>
              </Box>
              {isSectionOpen('admin', false) ? <ExpandLessIcon sx={{ fontSize: '1rem' }} /> : <ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
            </Typography>

            <Collapse in={isSectionOpen('admin', false)} timeout="auto">
              <List sx={{ px: 1.5 }}>
                <ListItemButton
                  data-nav-path="/systemkonfiguration"
                  selected={isActive('/systemkonfiguration')}
                  {...navProps('/systemkonfiguration')}
                  sx={navItemSx}
                >
                  <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><TuneIcon /></ListItemIcon>
                  <ListItemText primary="Systemkonfiguration" primaryTypographyProps={navTextProps} />
                </ListItemButton>

                <ListItemButton
                  data-nav-path="/email-templates"
                  selected={isActive('/email-templates')}
                  {...navProps('/email-templates')}
                  sx={navItemSx}
                >
                  <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><EmailIcon /></ListItemIcon>
                  <ListItemText primary="E-Mail Templates" primaryTypographyProps={navTextProps} />
                </ListItemButton>

                <ListItemButton
                  data-nav-path="/backups"
                  selected={isActive('/backups')}
                  {...navProps('/backups')}
                  sx={navItemSx}
                >
                  <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><BackupIcon /></ListItemIcon>
                  <ListItemText primary="Backups" primaryTypographyProps={navTextProps} />
                </ListItemButton>

                <ListItemButton
                  data-nav-path="/berechtigungsmatrix"
                  selected={isActive('/berechtigungsmatrix')}
                  {...navProps('/berechtigungsmatrix')}
                  sx={navItemSx}
                >
                  <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><TableChartIcon /></ListItemIcon>
                  <ListItemText primary="Berechtigungen" primaryTypographyProps={navTextProps} />
                </ListItemButton>

                <ListItemButton
                  data-nav-path="/debug-wartung"
                  selected={isActive('/debug-wartung')}
                  {...navProps('/debug-wartung')}
                  sx={navItemSx}
                >
                  <ListItemIcon sx={{ minWidth: 38, color: sidebarIcon }}><BugReportIcon /></ListItemIcon>
                  <ListItemText primary="Debug & Wartung" primaryTypographyProps={navTextProps} />
                </ListItemButton>
              </List>
            </Collapse>
          </>
        )}

      </Box>

      {/* Bottom branding */}
      <Box sx={{ flexShrink: 0, p: 2, borderTop: `1px solid ${borderColor}` }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
          <Typography
            {...navProps('/docs')}
            sx={{
              fontSize: '0.7rem',
              color: isActive('/docs') ? versionText : '#4caf50',
              cursor: 'pointer',
              '&:hover': { color: accent },
              transition: 'color 0.15s',
            }}
          >
            Docs
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: versionText }}>·</Typography>
          <Typography
            onClick={(event) => {
              event.stopPropagation();
              if (registerVersionClick()) {
                showInfo(`Entwicklermodus: ${DEV_MODE_LABEL}`);
              }
            }}
            sx={{
              fontSize: '0.7rem',
              color: devMode ? accent : versionText,
              cursor: 'pointer',
              userSelect: 'none',
              '&:hover': { color: accent },
            }}
          >
            {devMode ? DEV_MODE_LABEL : (version ? `STARK-AIO v${version}` : 'STARK-AIO')}
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
export { DRAWER_WIDTH };
